/**
 * 顶部标签栏状态 - Pinia store
 *
 * 记录已打开过的路由，配合 src/components/TagsView.vue 做标签切换与关闭。
 * 关闭标签会一并清掉该页在 keepAlive store 里的缓存与滚动位置，
 * 否则「关掉标签再打开」看到的还是关闭前的旧数据，与用户预期不符。
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useKeepAliveStore } from './keepAlive';
import type { TagView } from '@/types/tagsView';

export const useTagsViewStore = defineStore('tagsView', () => {
  /** 已打开的标签，按打开顺序排列，固定标签恒在首位 */
  const views = ref<TagView[]>([]);
  /** 固定标签（首页）path，不可关闭，「关闭其他」「关闭全部」都保留它 */
  const affixPath = ref('');

  /** 关闭标签时同步清掉该页的 KeepAlive 缓存与滚动位置 */
  const dropCache = (view: TagView) => {
    if (view.name) useKeepAliveStore().removeCache(view.name);
  };

  /** 设置固定标签 path，菜单异步加载完成后由 TagsView 调用 */
  const setAffix = (path: string) => {
    affixPath.value = path;
  };

  /**
   * 记录某菜单标签下最后停留的完整路径。
   * 供「点标签回到离开时的位置」用；标签不存在时忽略（还没打开过就不必记）。
   */
  const setLastPath = (path: string, fullPath: string) => {
    const target = views.value.find((item) => item.path === path);
    if (target) target.lastPath = fullPath;
  };

  /** 新增标签；已存在时只更新标题与 name（菜单加载完成后补全名称） */
  const addView = (view: TagView) => {
    const existing = views.value.find((item) => item.path === view.path);
    if (existing) {
      existing.title = view.title;
      existing.name = view.name;
      return;
    }
    if (view.path === affixPath.value) views.value.unshift(view);
    else views.value.push(view);
  };

  /**
   * 关闭单个标签，固定标签不可关
   * @returns 关的是当前页时返回应跳转的 path，否则返回 null（停在原页不跳转）
   */
  const closeView = (path: string, activePath: string): string | null => {
    if (path === affixPath.value) return null;
    const index = views.value.findIndex((item) => item.path === path);
    if (index === -1) return null;
    const [removed] = views.value.splice(index, 1);
    if (removed) dropCache(removed);
    if (path !== activePath) return null;
    /** 关掉当前页后优先落到右邻，没有则左邻，都没有则回固定标签 */
    const next = views.value[index] ?? views.value[index - 1];
    return next?.path ?? affixPath.value;
  };

  /** 关闭除固定标签和指定标签外的全部标签 */
  const closeOthers = (path: string) => {
    const kept: TagView[] = [];
    views.value.forEach((item) => {
      if (item.path === path || item.path === affixPath.value) kept.push(item);
      else dropCache(item);
    });
    views.value = kept;
  };

  /**
   * 关闭全部标签（固定标签除外）
   * @returns 应跳转的固定标签 path
   */
  const closeAll = (): string => {
    views.value.forEach((item) => {
      if (item.path !== affixPath.value) dropCache(item);
    });
    views.value = views.value.filter((item) => item.path === affixPath.value);
    return affixPath.value;
  };

  /** 清空标签，退出登录 / 切换账号时调用，避免新账号看到上一个账号的标签 */
  const clear = () => {
    views.value = [];
    affixPath.value = '';
  };

  return {
    views,
    affixPath,
    setAffix,
    setLastPath,
    addView,
    closeView,
    closeOthers,
    closeAll,
    clear,
  };
});
